import { Box, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import AddDamagedSectionDescription from '../../pages/DamageRegistration/AddDamagedSectionDescription'
import AddDamagedSectionPhoto from '../../pages/DamageRegistration/AddDamagedSectionPhoto'
import SelectDamagedSection from '../../pages/DamageRegistration/SelectDamagedSection'
import SelectDamagedSectionTypes from '../../pages/DamageRegistration/SelectDamagedSectionTypes'

import {
  fixRotation,
  getCoordinates,
  checkIsBase64,
  isFileTypeImage,
  removeMime,
} from '../../lib/helpers'
import { useAppStore } from '../../store/useAppStore'

import useDamageTypes from './useDamageTypes'
import { useTranslation } from 'react-i18next'
import React from 'react'

const useDamageRegistrationStep = (step: number) => {
  // External State
  const tabValue = useAppStore((state) => state.tabValue)
  const setSnackbar = useAppStore((state) => state.setSnackbar)
  const setLoading = useAppStore((state) => state.setLoading)

  const [coordinates, setCoordinates] = useState<number[] | null>(null)
  const [damagePhotos, setDamagePhotos] = useState([])
  const [descriptionInput, setDescriptionInput] = useState('')

  // Custom hooks
  const { damageTypeOptions, damageTypes, setDamageTypes } =
    useDamageTypes(tabValue)

  // Callbacks
  const handleDamagedSectionTypesSelect = useCallback(
    (event: { target: { name: any; checked: any } }) => {
      setDamageTypes((damageTypes) => ({
        ...damageTypes,
        [event.target.name]: event.target.checked,
      }))
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  )

  const { t } = useTranslation()

  const addNewDamage = (event, scale, positionX, positionY) => {
    let [x, y] = getCoordinates(event)
    const width = event.target.width
    const height = event.target.height
    const x_coordinate = (((-positionX + x) / scale) * 100) / width
    const y_coordinate = (((-positionY + y) / scale) * 100) / height

    setCoordinates([x_coordinate, y_coordinate] as number[])
  }

  const onImageUpload = useCallback(
    (event: { target: { files: string | any[]; value: null } }) => {
      setLoading(true)
      const onImageReaderLoad = () => (event: { target: { result: any } }) => {
        if (checkIsBase64(removeMime(event.target.result))) {
          setDamagePhotos((previousDamagePhotos) => [
            ...previousDamagePhotos,
            event.target.result,
          ])
          setLoading(false)
        } else {
          setSnackbar({
            open: true,
            message: t('snackbar.something_went_wrong'),
          })
          setLoading(false)
        }
      }
      if (event.target.files.length) {
        if (isFileTypeImage(event.target.files[0].type)) {
          let myRotationFunction = async function (ArrayOfFilesToBeRotated) {
            let blobOfArray = await fixRotation(ArrayOfFilesToBeRotated)
            const reader = new FileReader()

            reader.onload = onImageReaderLoad()

            reader.readAsDataURL(blobOfArray[0])
          }
          myRotationFunction([event.target.files[0]])
        }
      }

      event.target.value = null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const removeDamagePhoto = useCallback(
    (index) => {
      const newPhotos = damagePhotos.filter((_, i) => i !== index)
      setDamagePhotos(newPhotos)
    },
    [damagePhotos]
  )

  const handleDescriptionChange = useCallback(
    (event) => setDescriptionInput(event.target.value.slice(0, 300)),
    []
  )

  const atLeastOneDamageTypeSelected = Object.values(damageTypes).some(
    (type) => type
  )
  const isDamagePhotosAdded = damagePhotos.length > 0
  const isDamageDescriptionAdded = descriptionInput.length > 0
  const isButtonDisabled =
    (step === 1 && !coordinates) ||
    (step === 2 && !atLeastOneDamageTypeSelected) ||
    (step === 3 && !isDamagePhotosAdded)

  const progress = [
    coordinates,
    atLeastOneDamageTypeSelected,
    isDamagePhotosAdded,
    isDamageDescriptionAdded,
  ]

  const ongoingProgressValue = progress.filter((passed) => passed).length
  const allProgressSize = progress.length
  const currentProgressValue = (ongoingProgressValue / allProgressSize) * 100

  let title, content

  switch (step) {
    case 1:
      title = t('damage_registration_page.location_damage')
      content = (
        <SelectDamagedSection
          addNewDamage={addNewDamage}
          coordinates={coordinates}
        />
      )
      break
    case 2:
      title = t('damage_registration_page.type_of_damage')
      content = (
        <SelectDamagedSectionTypes
          handleChange={handleDamagedSectionTypesSelect}
          damageTypeOptions={damageTypeOptions}
        />
      )
      break
    case 3:
      title = t('damage_registration_page.damage_photo')
      content = (
        <AddDamagedSectionPhoto
          damagePhotos={damagePhotos}
          removeDamagePhoto={removeDamagePhoto}
          onImageUpload={onImageUpload}
        />
      )
      break
    case 4:
      title = t('damage_registration_page.description')
      content = (
        <AddDamagedSectionDescription
          descriptionInput={descriptionInput}
          handleDescriptionChange={handleDescriptionChange}
        />
      )
      break
    default:
      title = ''
      content = ''
      break
  }

  const currentStepScreen = (
    <Box
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      pt={1}
      paddingBottom={6}
      overflow="auto"
      px={2}
    >
      <Typography
        variant="h1"
        component="p"
        sx={{
          textAlign: 'left',
          width: '100%',
          margin: (theme) => theme.spacing(2, 0),
        }}
      >
        {title}
      </Typography>
      {content}
    </Box>
  )

  return {
    isButtonDisabled,
    coordinates,
    damageTypes,
    damagePhotos,
    descriptionInput,
    progress: currentProgressValue,
    currentStepScreen,
  }
}

export default useDamageRegistrationStep
